import { Link } from "react-router-dom";
import { Heart, GitCompare, MapPin } from "lucide-react";
import { media } from "@/lib/api";
import { inr, label, themeFor, FIG_TYPE } from "@/lib/fmt";
import { VerifPill, StatusPill } from "./Bits";

const BEST = ["independently_verified", "reviewed_against_documents", "under_review", "needs_revalidation"];
export function topVerification(v = {}) {
  for (const s of BEST) { const area = Object.keys(v).find((a) => v[a]?.status === s); if (area) return { area, status: s }; }
  return { area: null, status: "seller_provided" };
}

export default function ListingCard({ l, saved, onSave, inCompare, onCompare, catName }) {
  const d = l.data || {};
  const t = themeFor(l.category || d.category);
  const tv = topVerification(l.verification);
  const fin = l.fin || {};
  const period = fin.period_end ? `FY to ${new Date(fin.period_end).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}` : null;
  return (
    <article className="card overflow-hidden flex flex-col fade-up group" data-testid={`listing-card-${l.id}`} style={{ background: t.bg }}>
      <Link to={`/business/${l.slug}`} className="block p-2 pb-0" aria-label={d.title}>
        <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-[#E9DDCB]">
          {l.cover_image_id ? <img src={media(l.cover_image_id, "thumb")} alt={d.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
            : <div className="w-full h-full flex items-center justify-center text-sm text-stone-500">No approved photo yet</div>}
          <div className="absolute top-2 left-2 flex flex-wrap gap-1">
            {l.is_demo && <span className="chip bg-[#49392F] text-[#F7F3EB]" data-testid="card-demo-label">Demo listing — not available for purchase</span>}
          </div>
          {l.availability !== "available" && <div className="absolute bottom-2 left-2"><StatusPill s={l.availability} /></div>}
        </div>
      </Link>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <p className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ color: t.accent }}>
          {catName || label(d.category)} · <MapPin size={11} /> {d.city}
        </p>
        <Link to={`/business/${l.slug}`}><h3 className="font-display text-lg leading-snug text-[#292A25] hover:text-[#29483D]">{d.title}</h3></Link>
        <p className="font-display text-2xl text-[#29483D]" data-testid="card-asking-price">{inr(d.asking_price)}</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><p className="text-stone-500">Revenue</p><p className="font-semibold">{inr(fin.revenue)}</p></div>
          <div><p className="text-stone-500">{fin.profit_definition ? label(fin.profit_definition) : "Profit"}</p><p className="font-semibold">{inr(fin.profit)}</p></div>
        </div>
        <p className="text-[11px] text-stone-500">{fin.figure_type ? FIG_TYPE[fin.figure_type] : "Figures not provided"}{period ? ` · ${period}` : ""}</p>
        <div className="flex flex-wrap gap-1.5 mt-1">
          <VerifPill status={tv.status} />
          <span className="chip bg-white/70 text-[#49392F]">{d.sale_type === "asset_sale" ? "Asset sale" : "Full acquisition"}</span>
        </div>
        <div className="mt-auto pt-3 flex gap-2">
          {onSave && <button className={`btn-ghost btn-sm ${saved ? "!bg-[#29483D] !text-white" : ""}`} onClick={() => onSave(l)} aria-pressed={!!saved} data-testid={`save-btn-${l.id}`}><Heart size={14} /> {saved ? "Saved" : "Save"}</button>}
          {onCompare && <button className={`btn-ghost btn-sm ${inCompare ? "!bg-[#B59762] !text-white" : ""}`} onClick={() => onCompare(l)} aria-pressed={!!inCompare} data-testid={`compare-btn-${l.id}`}><GitCompare size={14} /> {inCompare ? "Comparing" : "Compare"}</button>}
        </div>
      </div>
    </article>
  );
}
